/**
 * ENTERPRISE-GRADE VALIDATION SYSTEM
 * Cyber Security + Accounting + Business Compliance
 * 
 * Security Features:
 * - Input sanitization against XSS, injection attacks
 * - Real-time character filtering
 * - Pattern-based validation
 * 
 * Accounting Features:
 * - Precise decimal handling (2 decimal places max)
 * - Proper number formatting
 * - Financial range validation
 * 
 * Business Features:
 * - Expense limit enforcement
 * - Category-specific validation
 * - Audit trail support
 */

// ============================================================================
// SECURITY CONSTANTS
// ============================================================================

export const FINANCIAL_LIMITS = {
  MAX_AMOUNT: 10000000, // 1 Crore INR
  MIN_AMOUNT: 0.01,     // 1 Paisa minimum
  MAX_QUANTITY: 999999,
  MIN_QUANTITY: 0.001,
  DECIMAL_PLACES: 2,
  MAX_GST_RATE: 28,     // Indian GST maximum
  MAX_DESCRIPTION_LENGTH: 120,
  MAX_PARTY_NAME_LENGTH: 100,
} as const;

// Strict numeric patterns for financial data
export const VALIDATION_PATTERNS = {
  // Only digits and single decimal point, no leading zeros except for decimals
  AMOUNT_REAL_TIME: /^(?!0\d)(\d{1,8})?(\.\d{0,2})?$/,
  
  // Final validation - must have at least one digit
  AMOUNT_FINAL: /^(?!0\d)\d{1,8}(\.\d{1,2})?$/,
  
  // Quantity can have up to 3 decimal places
  QUANTITY_REAL_TIME: /^(?!0\d)(\d{1,6})?(\.\d{0,3})?$/,
  QUANTITY_FINAL: /^(?!0\d)\d{1,6}(\.\d{1,3})?$/,
  
  // Shop/Party names - allow alphanumeric, spaces, and common business punctuation like & and parentheses
  PARTY_NAME: /^[a-zA-Z0-9\s\.\-\(\)&,']*$/,
  
  // Description - alphanumeric + basic punctuation, no special chars
  DESCRIPTION: /^[a-zA-Z0-9\s\.\,\-\(\)\/&']*$/,
  
  // Date validation - strict ISO format
  DATE_ISO: /^\d{4}-\d{2}-\d{2}$/,
  
  // Invoice number - alphanumeric with limited special chars
  INVOICE_NO: /^[a-zA-Z0-9\-\/]*$/,
} as const;

// Characters that are absolutely forbidden in financial inputs
// Note: '&', '(', ')', and single quote are removed from this list as they're valid in party names
export const FORBIDDEN_CHARS = [
  '=', '+', '-', '*', '/', '^', '%', 
  '<', '>', '!', '@', '#', '$',
  '[', ']', '{', '}', '|', '\\',
  '`', '~', ';', ':', '"', '?',
  'e', 'E', // Scientific notation prevention
];

// ============================================================================
// REAL-TIME INPUT SANITIZATION
// ============================================================================

/**
 * Real-time sanitizer for numeric inputs
 * Prevents invalid characters from being typed
 */
export function sanitizeNumericInput(value: string, type: 'amount' | 'quantity' = 'amount'): string {
  if (!value) return '';
  
  // Remove any forbidden characters immediately
  let sanitized = value;
  for (const char of FORBIDDEN_CHARS) {
    sanitized = sanitized.replace(new RegExp(`\\${char}`, 'g'), '');
  }
  
  // Remove any letters or other invalid chars
  sanitized = sanitized.replace(/[a-zA-Z]/g, '');
  
  // Ensure only one decimal point
  const parts = sanitized.split('.');
  if (parts.length > 2) {
    sanitized = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Apply pattern validation
  const pattern = type === 'amount' ? VALIDATION_PATTERNS.AMOUNT_REAL_TIME : VALIDATION_PATTERNS.QUANTITY_REAL_TIME;
  if (!pattern.test(sanitized)) {
    // Remove the last character if it makes the input invalid
    return sanitized.slice(0, -1);
  }
  
  return sanitized;
}

/**
 * Real-time sanitizer for text inputs
 */
export function sanitizeTextInput(value: string, type: 'party' | 'description' | 'invoice'): string {
  if (!value) return '';
  
  let sanitized = value.trim();
  
  // Remove dangerous characters (allow & and parentheses for party names, allow single quotes)
  sanitized = sanitized.replace(/[<>\"`;|\\]/g, '');
  
  // Apply pattern validation based on type
  let pattern: RegExp;
  let maxLength: number;
  
  switch (type) {
    case 'party':
      pattern = VALIDATION_PATTERNS.PARTY_NAME;
      maxLength = FINANCIAL_LIMITS.MAX_PARTY_NAME_LENGTH;
      break;
    case 'description':
      pattern = VALIDATION_PATTERNS.DESCRIPTION;
      maxLength = FINANCIAL_LIMITS.MAX_DESCRIPTION_LENGTH;
      break;
    case 'invoice':
      pattern = VALIDATION_PATTERNS.INVOICE_NO;
      maxLength = 50;
      break;
    default:
      return sanitized;
  }
  
  // Remove invalid characters
  if (!pattern.test(sanitized)) {
    sanitized = sanitized.replace(/[^a-zA-Z0-9\s\.\,\-\(\)\/&']/g, '');
  }
  
  // Enforce length limit
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return sanitized;
}

// ============================================================================
// ENTERPRISE VALIDATION FUNCTIONS
// ============================================================================

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  sanitizedValue?: string;
}

/**
 * Comprehensive amount validation
 * Cyber Security + Accounting Standards
 */
export function validateAmount(value: string | number, fieldName: string = 'Amount'): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!value || value === '') {
    return { isValid: false, errors: [`${fieldName} is required`], warnings: [] };
  }
  
  const stringValue = String(value).trim();
  
  // Security check - detect potential injection attempts
  if (FORBIDDEN_CHARS.some(char => stringValue.includes(char))) {
    return { 
      isValid: false, 
      errors: [`${fieldName} contains invalid characters. Only numbers and decimal point allowed.`], 
      warnings: [] 
    };
  }
  
  // Pattern validation
  if (!VALIDATION_PATTERNS.AMOUNT_FINAL.test(stringValue)) {
    return { 
      isValid: false, 
      errors: [`${fieldName} must be a valid number (max 2 decimal places)`], 
      warnings: [] 
    };
  }
  
  const numValue = parseFloat(stringValue);
  
  // Range validation
  if (isNaN(numValue)) {
    return { 
      isValid: false, 
      errors: [`${fieldName} must be a valid number`], 
      warnings: [] 
    };
  }
  
  if (numValue < FINANCIAL_LIMITS.MIN_AMOUNT) {
    return { 
      isValid: false, 
      errors: [`${fieldName} must be at least ₹${FINANCIAL_LIMITS.MIN_AMOUNT}`], 
      warnings: [] 
    };
  }
  
  if (numValue > FINANCIAL_LIMITS.MAX_AMOUNT) {
    return { 
      isValid: false, 
      errors: [`${fieldName} cannot exceed ₹${FINANCIAL_LIMITS.MAX_AMOUNT.toLocaleString('en-IN')}`], 
      warnings: [] 
    };
  }
  
  // Business logic warnings
  if (numValue > 100000) {
    warnings.push(`${fieldName} is quite high (₹${numValue.toLocaleString('en-IN')}). Please verify.`);
  }
  
  // Ensure proper decimal places
  const decimalPlaces = (stringValue.split('.')[1] || '').length;
  if (decimalPlaces > FINANCIAL_LIMITS.DECIMAL_PLACES) {
    const sanitized = numValue.toFixed(FINANCIAL_LIMITS.DECIMAL_PLACES);
    return {
      isValid: true,
      errors: [],
      warnings: [`${fieldName} rounded to ${FINANCIAL_LIMITS.DECIMAL_PLACES} decimal places`],
      sanitizedValue: sanitized
    };
  }
  
  return { isValid: true, errors: [], warnings: [] };
}

/**
 * GST amount validation with business rules
 */
export function validateGSTAmount(gstAmount: string, grossAmount: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!gstAmount || gstAmount.trim() === '') {
    return { isValid: true, errors: [], warnings: [] }; // GST is optional
  }
  
  // Allow zero as valid GST amount (for GST-exempt items with GST enabled)
  const gstNum = parseFloat(gstAmount);
  if (gstNum === 0) {
    return { isValid: true, errors: [], warnings: [] };
  }
  
  // For non-zero amounts, apply standard amount validation
  const basicValidation = validateAmount(gstAmount, 'GST Amount');
  if (!basicValidation.isValid) {
    return basicValidation;
  }
  
  const grossNum = parseFloat(grossAmount || '0');
  
  // Business rule: GST should not exceed gross amount
  if (grossNum > 0 && gstNum > grossNum) {
    errors.push('GST amount cannot exceed gross amount');
  }
  
  // Business rule: Calculate GST rate and warn if unusual
  if (grossNum > 0) {
    const gstRate = (gstNum / grossNum) * 100;
    
    if (gstRate > FINANCIAL_LIMITS.MAX_GST_RATE) {
      errors.push(`GST rate (${gstRate.toFixed(1)}%) exceeds maximum allowed rate of ${FINANCIAL_LIMITS.MAX_GST_RATE}%`);
    } else if (gstRate > 28.5) {
      warnings.push(`GST rate (${gstRate.toFixed(1)}%) is unusually high. Please verify.`);
    } else if (gstRate > 0 && gstRate < 1) {
      warnings.push(`GST rate (${gstRate.toFixed(1)}%) is unusually low. Please verify.`);
    }
  }
  
  return { isValid: errors.length === 0, errors, warnings };
}

/**
 * Quantity validation for unit-based calculations
 */
export function validateQuantity(value: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!value || value === '') {
    return { isValid: false, errors: ['Quantity is required'], warnings: [] };
  }
  
  const stringValue = String(value).trim();
  
  // Security check
  if (FORBIDDEN_CHARS.some(char => stringValue.includes(char))) {
    return { 
      isValid: false, 
      errors: ['Quantity contains invalid characters. Only numbers and decimal point allowed.'], 
      warnings: [] 
    };
  }
  
  // Pattern validation
  if (!VALIDATION_PATTERNS.QUANTITY_FINAL.test(stringValue)) {
    return { 
      isValid: false, 
      errors: ['Quantity must be a valid number (max 3 decimal places)'], 
      warnings: [] 
    };
  }
  
  const numValue = parseFloat(stringValue);
  
  if (isNaN(numValue) || numValue <= 0) {
    return { 
      isValid: false, 
      errors: ['Quantity must be greater than 0'], 
      warnings: [] 
    };
  }
  
  if (numValue < FINANCIAL_LIMITS.MIN_QUANTITY) {
    return { 
      isValid: false, 
      errors: [`Quantity must be at least ${FINANCIAL_LIMITS.MIN_QUANTITY}`], 
      warnings: [] 
    };
  }
  
  if (numValue > FINANCIAL_LIMITS.MAX_QUANTITY) {
    return { 
      isValid: false, 
      errors: [`Quantity cannot exceed ${FINANCIAL_LIMITS.MAX_QUANTITY}`], 
      warnings: [] 
    };
  }
  
  // Business warnings
  if (numValue > 10000) {
    warnings.push(`Quantity is quite high (${numValue}). Please verify.`);
  }
  
  return { isValid: true, errors: [], warnings: [] };
}

/**
 * Date validation with business rules
 */
export function validateExpenseDate(dateString: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!dateString) {
    return { isValid: false, errors: ['Date is required'], warnings: [] };
  }
  
  // Pattern validation
  if (!VALIDATION_PATTERNS.DATE_ISO.test(dateString)) {
    return { 
      isValid: false, 
      errors: ['Date must be in valid format (YYYY-MM-DD)'], 
      warnings: [] 
    };
  }
  
  const date = new Date(dateString);
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return { 
      isValid: false, 
      errors: ['Invalid date'], 
      warnings: [] 
    };
  }
  
  // Future date check
  if (date > today) {
    return { 
      isValid: false, 
      errors: ['Expense date cannot be in the future'], 
      warnings: [] 
    };
  }
  
  // Very old date warning
  if (date < oneYearAgo) {
    warnings.push('Expense date is more than 1 year old. Please verify.');
  }
  
  return { isValid: true, errors: [], warnings: [] };
}

// ============================================================================
// REACT HOOKS FOR REAL-TIME VALIDATION
// ============================================================================

/**
 * Custom hook for enterprise-grade form validation
 */
export function useEnterpriseValidation() {
  const validateField = (value: any, type: string, additionalParams?: any) => {
    switch (type) {
      case 'amount':
      case 'gross':
      case 'rate':
        return validateAmount(value, type.charAt(0).toUpperCase() + type.slice(1));
      
      case 'gstAmount':
        return validateGSTAmount(value, additionalParams?.grossAmount || '');
      
      case 'quantity':
        return validateQuantity(value);
      
      case 'date':
        return validateExpenseDate(value);
      
      default:
        return { isValid: true, errors: [], warnings: [] };
    }
  };
  
  const sanitizeField = (value: string, type: string) => {
    switch (type) {
      case 'amount':
      case 'gross':
      case 'rate':
      case 'gstAmount':
        return sanitizeNumericInput(value, 'amount');
      
      case 'quantity':
        return sanitizeNumericInput(value, 'quantity');
      
      case 'party':
      case 'shop':
        return sanitizeTextInput(value, 'party');
      
      case 'description':
        return sanitizeTextInput(value, 'description');
      
      case 'invoiceNo':
        return sanitizeTextInput(value, 'invoice');
      
      default:
        return value;
    }
  };
  
  return { 
    validateField, 
    sanitizeField,
    sanitizeInput: sanitizeField, // Alias for backward compatibility
    isInputSecure: (value: string) => {
      if (!value) return true;
      // Check if input contains any forbidden characters
      return !FORBIDDEN_CHARS.some(char => value.includes(char));
    },
    formatAmount: (value: string) => {
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return num.toFixed(2);
    },
    getValidationMessage: (errors: string[]) => {
      return errors.length > 0 ? errors[0] : '';
    }
  };
}

export default {
  validateAmount,
  validateGSTAmount,
  validateQuantity,
  validateExpenseDate,
  sanitizeNumericInput,
  sanitizeTextInput,
  FINANCIAL_LIMITS,
  VALIDATION_PATTERNS,
  FORBIDDEN_CHARS,
};
